import { useEffect } from "react";
import { RefreshCcw, AlertCircle, CreditCard, Mail, ShieldAlert, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Refund() {
  // Fix: Ensures the page starts at the header on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="bg-[#fdfcf7] min-h-screen pb-20 font-sans">
      {/* 
          HERO / HEADER SECTION 
      */}
      <section className="py-20 px-6 border-b-4 border-foreground bg-accent/5">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-block px-3 py-1 bg-foreground text-white font-black uppercase text-xs tracking-widest mb-6 border-2 border-foreground shadow-brutal-sm">
              Billing Standards
            </div>
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4">
              Refund <span className="text-primary italic">Policy.</span>
            </h1>
            <p className="font-bold text-muted-foreground flex items-center gap-2 uppercase text-sm tracking-tight">
              <RefreshCcw className="w-4 h-4 text-primary" /> Last modified: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 
          POLICY CONTENT 
      */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid gap-8">
          
          {/* General Terms Card */}
          <div className="bg-white border-4 border-foreground p-8 shadow-brutal relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 -mr-16 -mt-16 rounded-full" />
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <ShieldAlert className="w-10 h-10 text-primary" />
                <h2 className="text-3xl font-black uppercase tracking-tighter italic">Standard Policy</h2>
              </div>
              <p className="text-xl font-bold leading-relaxed text-foreground mb-4">
                DmFlio provides digital SaaS (Software as a Service) solutions. Due to the immediate activation of automation resources and API infrastructure, all subscription payments are generally <span className="text-primary">non-refundable.</span>
              </p>
              <p className="font-bold text-muted-foreground">
                By subscribing to our services, you acknowledge that the digital nature of the platform prevents the "return" of utilized resources.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Tier Section */}
            <div className="bg-mint border-4 border-foreground p-8 shadow-brutal">
              <div className="w-12 h-12 bg-white border-2 border-foreground flex items-center justify-center mb-6 shadow-brutal-sm">
                <CheckCircle2 className="w-7 h-7 text-foreground" />
              </div>
              <h3 className="text-2xl font-black uppercase mb-4 tracking-tight">1. Evaluation Period</h3>
              <p className="font-bold text-muted-foreground leading-relaxed">
                To ensure DmFlio meets your requirements, we provide a robust <span className="text-foreground underline underline-offset-2">Free Plan.</span> We strongly encourage all users to evaluate the platform’s features and compatibility with their Instagram workflows before upgrading to a Pro subscription.
              </p>
            </div>

            {/* Eligibility Section */}
            <div className="bg-butter border-4 border-foreground p-8 shadow-brutal">
              <div className="w-12 h-12 bg-white border-2 border-foreground flex items-center justify-center mb-6 shadow-brutal-sm">
                <CreditCard className="w-7 h-7 text-foreground" />
              </div>
              <h3 className="text-2xl font-black uppercase mb-4 tracking-tight">2. Refund Eligibility</h3>
              <p className="font-bold text-muted-foreground leading-relaxed mb-4">
                Exceptions to the non-refundable policy are only considered under the following technical conditions:
              </p>
              <ul className="space-y-2 font-black uppercase text-xs tracking-wide">
                <li className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-primary" /> Duplicate billing transactions</li>
                <li className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-primary" /> Documented system failures</li>
                <li className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-primary" /> Unauthorized account activity</li>
              </ul>
            </div>
          </div>

          {/* Contact / Dispute Section */}
          <div className="bg-foreground text-white border-4 border-foreground p-10 shadow-brutal flex flex-col md:flex-row items-center justify-between gap-8 mt-8">
            <div className="space-y-4">
              <h2 className="text-3xl font-black uppercase tracking-tighter">Dispute Resolution</h2>
              <p className="font-bold text-gray-400 max-w-md">
                If you believe there has been a billing error, please contact our financial department immediately with your transaction ID.
              </p>
            </div>
            <div className="bg-white text-foreground p-6 border-2 border-foreground shadow-[4px_4px_0px_0px_rgba(255,92,0,1)] min-w-[280px]">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-primary" />
                <span className="font-black uppercase text-xs tracking-widest">Support Email</span>
              </div>
              <p className="text-xl font-bold">hello@dmflio.com</p>
              <p className="text-[10px] uppercase font-black text-muted-foreground mt-2">Attention: Billing Department</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}